/**
 * ALERON quick add.
 *
 * A small "+" on each product card opens a minimal size list: a popover above
 * the button on desktop, a bottom sheet on mobile. Choosing a size adds it to
 * the cart over the Ajax Cart API and opens the cart drawer.
 *
 * The panel uses the native Popover API, which puts it in the top layer. That
 * matters here: the card clips its image with overflow:hidden and the grid
 * creates stacking contexts, so an in-flow panel would be cut off or trapped
 * behind the next row. It also brings light-dismiss and Escape for free.
 *
 * The "+" toggles the panel through the platform's own `popovertarget`
 * attribute rather than through script, so it keeps working before this
 * module has loaded.
 *
 * Adding to the cart follows the same contract as Horizon's own product form
 * — dispatch a CartLinesUpdateEvent carrying a deferred promise, then resolve
 * it with the refreshed cart — so the cart drawer, the bag count and anything
 * else listening all update from one event, whoever triggered it.
 */

import { CartLinesUpdateEvent } from '@shopify/events';
import { openPanel } from '@theme/aleron-overlay';

function themeRoutes() {
  return window.Theme?.routes ?? {};
}

/** Gap between the "+" button and the popover, in px. */
const OFFSET = 8;

class AleronQuickAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-aleron-quick-add-button]');
    this.panel = this.querySelector('[data-aleron-quick-add-panel]');
    if (!this.button || !this.panel) return;

    this.addEventListener('click', this.#handleClick);
    this.panel.addEventListener('toggle', this.#handleToggle);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.panel?.removeEventListener('toggle', this.#handleToggle);
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const size = target.closest('[data-variant-id]');
    if (!size) return;

    // Only the size buttons are handled here. The "+" is left alone on
    // purpose: it carries `popovertarget`, and calling preventDefault() on it
    // cancels the browser's own popover toggle, so the panel never opens.
    //
    // Nothing here needs to stop the card's link either — the quick add is a
    // sibling of it in snippets/aleron-product-card.liquid, not a descendant,
    // so a click in this subtree never reaches the link to begin with.
    event.preventDefault();
    this.#add(size);
  };

  #handleToggle = (event) => {
    if (event.newState !== 'open') return;
    // Only the desktop popover needs placing; the mobile sheet is pinned to
    // the bottom of the viewport by CSS.
    if (window.matchMedia('(min-width: 750px)').matches) this.#position();
  };

  /**
   * Places the popover above the "+" button, flipping below it when there is
   * not enough room, and clamping so it never leaves the viewport.
   */
  #position() {
    const anchor = this.button.getBoundingClientRect();
    const panel = this.panel.getBoundingClientRect();

    let top = anchor.top - panel.height - OFFSET;
    if (top < OFFSET) top = anchor.bottom + OFFSET;

    let left = anchor.right - panel.width;
    left = Math.max(OFFSET, Math.min(left, window.innerWidth - panel.width - OFFSET));

    this.panel.style.top = `${top}px`;
    this.panel.style.left = `${left}px`;
  }

  /** @param {Element} sizeButton */
  async #add(sizeButton) {
    if (sizeButton.hasAttribute('disabled') || this.busy) return;

    const variantId = sizeButton.getAttribute('data-variant-id');
    if (!variantId) return;

    this.busy = true;
    this.setAttribute('data-busy', '');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise
      })
    );

    try {
      const response = await fetch(themeRoutes().cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });

      const result = await response.json();

      // The Cart API answers 422 with a `status` field rather than throwing.
      if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aleron-quick-add',
          sourceId: this.id || 'quick-add',
          itemCount: 1,
          productId: this.dataset.productId,
          didError: false
        }
      });

      this.panel.hidePopover();
      openPanel('cart-drawer');
    } catch (error) {
      deferred.reject(error);
      this.#showError(error);
    } finally {
      this.busy = false;
      this.removeAttribute('data-busy');
    }
  }

  #fetchCart() {
    // Reuse the cart component's own fetch when it exists, so its internal
    // state stays in step rather than being refreshed twice.
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems?.fetchCartData) return cartItems.fetchCartData();

    return fetch(`${themeRoutes().cart_url || '/cart'}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  #showError(error) {
    const region = this.querySelector('[data-aleron-quick-add-error]');
    if (!region) return;

    region.textContent = error?.message || '';
    region.hidden = false;
    setTimeout(() => {
      region.hidden = true;
      region.textContent = '';
    }, 5000);
  }
}

if (!customElements.get('aleron-quick-add')) customElements.define('aleron-quick-add', AleronQuickAdd);
